import { http } from './http';
import { KIBANA_EVENT, NAME, SCHEDULE_DOMAIN, setLoggerProxy } from '../common/constants';
import ErrorCode from '../common/error-code';
import { ErrorName } from '../common/error-info';
import RtcError from '../common/rtc-error';
import { generateErrorMessage } from '../common/error-generator';
import loggerManager from './log/logger-manager';
import promiseRetry from './retry';
import { fibonacci, isArray, isBoolean, isNumber, isOverseaSdkAppId, performanceNow, promiseAny } from './utils';
import innerEmitter from '../inner-emitter/inner-emitter';
import { INNER_EVENT } from '../inner-emitter/inner-type';
import { ConnectionState } from '..';
import eventLog from './log/event-log';
import { getDeviceModelFromUA, getGPUInfo, getOSString, getUserAgentData } from './environment';

let scheduleResult: ScheduleResult | null = null;
let scheduleCacheExpireIn = 0;
const SCHEDULE_CACHE_EXPIRE_TIME = 48 * 3600 * 1000;
const LOCAL_STORAGE_CACHE_EXPIRE_TIME = 1 * 3600 * 1000; // 1 hour for localStorage cache
const SCHEDULE_CACHE_KEY = 'trtc_schedule_cache';
export let isNeedToSchedule = true;

// 启用 DNS 预缓存可以提前建连 和 DNS 解析，降低首次进房耗时。
if (typeof document !== 'undefined') {
  document.head.insertAdjacentHTML('beforeend', Object.values(SCHEDULE_DOMAIN)
    .map(domain => `<link rel="dns-prefetch" href="https://${domain}">`)
    .join('\r\n'));
}

// 初始化时从 localStorage 加载调度缓存
loadScheduleFromLocalStorage();

/**
 * 从 localStorage 加载调度缓存
 */
function loadScheduleFromLocalStorage(): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }
  try {
    const cached = localStorage.getItem(SCHEDULE_CACHE_KEY);
    if (cached) {
      const { result, expireIn } = JSON.parse(cached);
      if (expireIn > Date.now()) {
        scheduleResult = result;
        scheduleCacheExpireIn = expireIn;
        isNeedToSchedule = false;
      } else {
        localStorage.removeItem(SCHEDULE_CACHE_KEY);
      }
    }
  } catch (error) {
    loggerManager.error('loadScheduleFromLocalStorage error', error);
  }
}

/**
 * 保存调度结果到 localStorage
 */
function saveScheduleToLocalStorage(result: ScheduleResult): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }
  try {
    if (result.config?.scheduleCache) {
      const expireIn = Date.now() + LOCAL_STORAGE_CACHE_EXPIRE_TIME;
      localStorage.setItem(SCHEDULE_CACHE_KEY, JSON.stringify({
        result,
        expireIn
      }));
      scheduleCacheExpireIn = expireIn;
    } else {
      localStorage.removeItem(SCHEDULE_CACHE_KEY);
    }
  } catch (error) {
    loggerManager.error('saveScheduleToLocalStorage error', error);
  }
}

/**
 * 清除 localStorage 中的调度缓存
 */
function clearScheduleCache(): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(SCHEDULE_CACHE_KEY);
  } catch (error) {
    loggerManager.error('clearScheduleCache error', error);
  }
}

/**
 * 设置下次进房是否要发起调度请求
 * @param {boolean} value
 */
export function setIsNeedToSchedule(value: boolean) {
  if (isBoolean(value) && value !== isNeedToSchedule) {
    isNeedToSchedule = value;
    loggerManager.info(`setIsNeedToSchedule ${value}`);
    if (!value) {
      scheduleCacheExpireIn = Date.now() + SCHEDULE_CACHE_EXPIRE_TIME;
    } else {
      clearScheduleCache();
    }
  }
}

// 出现进房失败、推拉流失败、信令重连、PeerConnection 重连，则下次进房重新调度。
innerEmitter.on(INNER_EVENT.JOIN_FAILED, () => setIsNeedToSchedule(true));
innerEmitter.on(INNER_EVENT.PUBLISH_FAILED, () => setIsNeedToSchedule(true));
innerEmitter.on(INNER_EVENT.SUBSCRIBE_FAILED, () => setIsNeedToSchedule(true));
innerEmitter.on(INNER_EVENT.SIGNAL_CONNECTION_STATE_CHANGED, event => {
  if (event.state === ConnectionState.RECONNECTING) {
    setIsNeedToSchedule(true);
  }
});
innerEmitter.on(INNER_EVENT.PEER_CONNECTION_STATE_CHANGED, event => {
  if (event.state === ConnectionState.RECONNECTING) {
    setIsNeedToSchedule(true);
  }
});

function getScheduleDetailCost(sdkAppId: number, userId: string, starTime: number): ScheduleDetailCost {
  let result: ScheduleDetailCost = {
    totalCost: 0,
    local: 0,
    dns: 0,
    tcp: 0,
    tls: 0,
    request: 0,
    response: 0
  };
  try {
    const resources = performance.getEntriesByType('resource') as PerformanceSourceEntry[];
    const mainScheduleURL = getScheduleUrl(sdkAppId, ScheduleRequestType.CONFIG, NAME.MAIN);
    const backupScheduleURL = getScheduleUrl(sdkAppId, ScheduleRequestType.CONFIG, NAME.BACKUP);
    for (const item of resources) {
      if (item.startTime >= starTime && (item.name === mainScheduleURL || item.name === backupScheduleURL) && item.transferSize > 0) {
        const scheduleType = item.name === mainScheduleURL ? NAME.MAIN : NAME.BACKUP;
        const totalCost = Math.round(item.duration);
        const beforeDNS = Math.round(item.domainLookupStart - item.startTime);
        // @ts-ignore
        const redirect = item.redirectStart > 0 ? Math.round(item.redirectEnd - item.redirectStart) : 0;
        // @ts-ignore
        const httpCache = item.fetchStart > 0 ? Math.round(item.domainLookupStart - item.fetchStart) : 0;
        const dns = Math.round(item.domainLookupEnd - item.domainLookupStart);
        const tls = Math.round(item.requestStart - item.secureConnectionStart);
        const tcp = Math.round(item.secureConnectionStart - item.connectStart);
        const request = Math.round(item.responseStart - item.requestStart);
        const response = Math.round(item.responseEnd - item.responseStart);

        const data = [dns, tls, tcp, request, response];
        eventLog.uploadEvent({ log: `stat-schedule-net:${totalCost}(${beforeDNS}(${redirect}->${httpCache})->${data.join('->')}) ${scheduleType}`, userId });

        result = { ...result, totalCost, local: beforeDNS, dns, tcp, tls, request, response };
        break;
      }
    }
  } catch (error) {
    loggerManager.error('getScheduleDetailCost error', error);
  }
  return result;
}

interface ScheduleRequestParam {
  userId: string;
  sdkAppId: number | string;
  useStringRoomId: boolean;
  roomId: number | string;
  userSig: string;
  version: string;
  frameWorkType?: number;
  role?: number;
  latencyLevel?: number;
  prelink?: boolean;
}

/**
 * 请求后台调度服务，获取 websocket 域名、turn server 地址。
 * @export
 * @param {Object} options
 * @param {String} options.userId
 * @param {String} options.sdkAppId
 * @param {Boolean} options.useStringRoomId 是否使用字符串房间号
 * @param {String} options.roomId 房间号
 * @param {String} options.userSig userSig
 * @return {String[]}
 */
export async function sendScheduleRequest(params: ScheduleRequestParam): Promise<{ isCached: boolean; result: ScheduleResult; detailCost?: ScheduleDetailCost; }> {
  const {
    userId,
    sdkAppId,
    useStringRoomId,
    roomId,
    userSig,
    version,
    frameWorkType,
    role,
    latencyLevel
  } = params;
  if (!isNeedToSchedule && scheduleResult && scheduleCacheExpireIn > Date.now()) {
    // 若使用缓存，则异步拉取一次云控配置更新缓存，保证下次进房能使用到最新的云控配置
    // config: 完整的调度配置（包含 websocket 域名、turn server 等），通过 /api/v1/config 获取
    // cloudconfig: 仅云控配置部分（scheduleCache 等开关配置），通过 /api/v1/cloudconfig 获取
    sendCloudConfigRequest(params);
    return { isCached: true, result: scheduleResult };
  }

  // 统计信令调度的结果，包括各域名请求次数、耗时、error 等信息。
  const stat = {
    delta: 0,
    count: [1, 1],
    msg: [],
    detail: []
  };
  try {
    const data = await getScheduleFormData(params);
    const startTime = performanceNow();
    const result = await sendScheduleRequestWithRacing(data, stat, sdkAppId);
    if (result.config) {
      if (result.config.loggerDomain) {
        setLoggerProxy(result.config.loggerDomain);
      }
      if (isBoolean(result.config.scheduleCache)) {
        setIsNeedToSchedule(!result.config.scheduleCache);
      }
    }
    // 信令调度成功
    stat.delta = performanceNow() - startTime;
    const detailCost = getScheduleDetailCost(Number(sdkAppId), userId, startTime);
    scheduleResult = result;
    saveScheduleToLocalStorage(result as ScheduleResult);
    return { isCached: false, result: result as ScheduleResult, detailCost };
  } catch (error) {
    // promiseAny 会 reject error 数组
    const tempError = isArray(error) ? error[0] : error;
    const code = isNumber(tempError.code) ? tempError.code : 0;
    const message = `schedule failed${tempError.message ? `: ${tempError.message}` : ''}`;

    const rtcError = new RtcError({
      code: ErrorCode.SCHEDULE_FAILED,
      extraCode: code,
      message: generateErrorMessage({
        key: ErrorName.JOIN_ROOM_FAILED,
        data: { error: message, code }
      })
    });
    loggerManager.error(message, code);
    throw rtcError;
  }
}
export const scheduleProxy = {
  main: '',
  backup: ''
};
export function setScheduleProxy(proxy: string | string[]) {
  if (isArray(proxy)) {
    scheduleProxy.main = proxy[0];
    scheduleProxy.backup = proxy[1];
  } else {
    scheduleProxy.main = proxy;
    scheduleProxy.backup = proxy;
  }
}
export const enum ScheduleRequestType {
  CONFIG = 'config',
  TRTC_AUTO_CONF = 'trtcAutoConf',
  AUDIO_AI_AUTH = 'audioAiAuth',
  CLOUD_CONFIG = 'cloudconfig'
}

/**
 * 异步获取云控配置，用于更新缓存
 * @param {ScheduleRequestParam} params
 */
async function sendCloudConfigRequest(params: ScheduleRequestParam): Promise<void> {
  const { sdkAppId } = params;

  try {
    const data = await getScheduleFormData(params);
    const result = await doSendScheduleRequest(getScheduleUrl(sdkAppId, ScheduleRequestType.CLOUD_CONFIG, NAME.MAIN), data, { timeout: 5000 }) as any;

    if (result && result.config && scheduleResult) {
      loggerManager.info('update cloud config success');
      scheduleResult.config = { ...result.config };
      setIsNeedToSchedule(!result.config.scheduleCache);
      saveScheduleToLocalStorage(scheduleResult);
    }
  } catch (error) {
    loggerManager.warn('update cloud config failed', error);
  }
}

async function getScheduleFormData(params: ScheduleRequestParam): Promise<FormData> {
  const { userId, sdkAppId, useStringRoomId, roomId, userSig, version, frameWorkType, role, latencyLevel } = params;
  const data = new FormData();
  data.append('userId', String(userId));
  data.append('sdkAppId', String(sdkAppId));
  data.append('isStrGroupId', String(useStringRoomId));
  data.append('groupId', String(roomId));
  data.append('sdkVersion', version);
  data.append('userSig', String(userSig));

  const model = (await getUserAgentData())?.model || getDeviceModelFromUA();
  model && data.append('model', model);
  const osString = getOSString();
  osString && data.append('osString', osString);
  const gpu = getGPUInfo();
  gpu && data.append('gpu', gpu);
  role && data.append('role', String(role));
  latencyLevel && data.append('latencyLevel', String(latencyLevel));
  frameWorkType && data.append('frameWorkType', String(frameWorkType));
  return data;
}

/**
 * 获取信令调度请求 url，sdkappid 小于14亿则为海外的 sdkappid，请求海外的信令调度服务。
 * @param {Number} sdkAppId
 * @param {'main'|'backup'} type
 */
function getScheduleUrl(sdkAppId: number | string, name: ScheduleRequestType, type: 'main' | 'backup' = NAME.MAIN, getMainBackup = false) {
  return `https://${scheduleProxy[type] || getScheduleDomain(sdkAppId, type, getMainBackup)}/api/v1/${name}`;
}

export function getAbilityConfig<T>(sdkAppId: number | string, name: ScheduleRequestType, params: any) {
  const configDomain = getScheduleUrl(sdkAppId, name);
  const backupConfigDomain = getScheduleUrl(sdkAppId, name, NAME.BACKUP);
  const urlParams = new URLSearchParams(params).toString();
  const mainRequest = fetch(`${configDomain}?${urlParams}`).then(res => res.json());
  const backupRequest = fetch(`${backupConfigDomain}?${urlParams}`).then(res => res.json());
  return promiseAny<T>([mainRequest, backupRequest]);
}

export function getScheduleDomain(sdkAppId: number | string, type: 'main' | 'backup' = NAME.MAIN, getMainBackup = false) {
  let domain;
  if (isOverseaSdkAppId(sdkAppId)) {
    if (getMainBackup) {
      domain = type === NAME.MAIN ? SCHEDULE_DOMAIN.MAIN_OVERSEA_BACKUP : SCHEDULE_DOMAIN.BACKUP_OVERSEA;
    } else {
      domain = type === NAME.MAIN ? SCHEDULE_DOMAIN.MAIN_OVERSEA : SCHEDULE_DOMAIN.BACKUP_OVERSEA;
    }
  } else {
    domain = type === NAME.MAIN ? SCHEDULE_DOMAIN.MAIN : SCHEDULE_DOMAIN.BACKUP;
  }
  return domain;
}

/**
 * 发送信令调度请求
 * @param {String} url
 * @param {FormData} data
 * @param {Object} config
 * @return {Promise}
 */
function doSendScheduleRequest(url: string, data: FormData, config: any) {
  return new Promise((resolve, reject) => {
    http({ url, body: data, timeout: config.timeout, priority: 'high' })
      .then(res => {
        if (res.data.code === 0) {
          resolve(res.data.data);
        } else {
          reject({ code: res.data.code, message: res.data.msg });
        }
      })
      .catch(reject);
  });
}

/**
 * 请求信令调度，重试3次
 * @param {Function} onRetrying
 * @param {Function} onError
 * @returns Promise
 */
const doSendScheduleRequestWithRetry = (
  onRetrying: (retryCount: number, stopRetry?: () => void) => void,
  onError: (params: { error: any, retry: () => void, reject: (reason?: any) => void, retryFuncArgs: any[], retriedCount: number; }) => void
) => promiseRetry({
  retryFunction: doSendScheduleRequest,
  settings: { retries: 3, timeout: 0 },
  onError,
  onRetrying
});

/**
 * 请求信令调度，同时发两个请求竞速，并带有重试。
 * @param {Object} data
 * @param {Object} stat
 * @returns Promise
 */
function sendScheduleRequestWithRacing(data: FormData, stat: any, sdkAppId: number | string): Promise<ScheduleResult> {
  return new Promise((resolve, reject) => {
    let result: any = null;
    // 使用 promiseAny 同时发两个不同域名请求竞速
    promiseAny([
      doSendScheduleRequestWithRetry(
        retryCount => (stat.count[0] = retryCount + 1),
        ({ error, retry, retriedCount, retryFuncArgs }) => {
          stat.msg[0] = error.message;
          // 有 result 代表请求调度成功，成功后无需再重试
          if (!result) {
            // 对于国际站，最后两次重试，将使用备选域名
            if (retriedCount >= 1) {
              // 替换 url 参数
              retryFuncArgs[0] = getScheduleUrl(sdkAppId, ScheduleRequestType.CONFIG, NAME.MAIN, true);
            }
            retry();
          }
        }
      )(getScheduleUrl(sdkAppId, ScheduleRequestType.CONFIG, NAME.MAIN), data, {
        get timeout() {
          return fibonacci(2 + stat.count[0]) * 1000;
        }
      }),
      doSendScheduleRequestWithRetry(
        retryCount => (stat.count[1] = retryCount + 1),
        ({ error, retry, retriedCount, retryFuncArgs }) => {
          stat.msg[1] = error.message;
          // 调度成功后，无需再重试
          if (!result) {
            // 对于国际站，最后一次重试用保底的域名
            if (retriedCount >= 2) {
              // 替换 url 参数
              retryFuncArgs[0] = getScheduleUrl(sdkAppId, ScheduleRequestType.CONFIG, NAME.BACKUP, true);
            }
            retry();
          }
        }
      )(getScheduleUrl(sdkAppId, ScheduleRequestType.CONFIG, NAME.BACKUP), data, {
        get timeout() {
          return fibonacci(2 + stat.count[1]) * 1000;
        }
      })
    ])
      .then(res => {
        result = res;
        resolve(result);
      })
      .catch(reject);
  });
}
